import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { invokeFunction, toArray } from '../../utilities';
import useOverlayState from './useOverlayState';

// This component can be used in controlled or uncontrolled modes.
// - Controlled mode
//   You can pass in `showOverlay` to fully control the state of
//   the overlay. In addition you need to pass in `onOverlayToggle`
//   to listen to the overlay toggle event to refresh the state you
//   maintain.
// - Uncontrolled mode
//   The trigger component maintains the overlay state inside. You
//   can pass in `triggered` to set an initial state for the overlay.
//   And whether listen to the overlay toggle event is up to you.
const OverlayTrigger = (props, ref) => {
  let {
    children,
    onOverlayToggle,
    renderOverlay,
    showOverlay,
    triggered = !!showOverlay,
    triggerEvents = 'click', // can be Array, e.g ["mouseenter", "mouseleave"]
    triggerToggle = false
  } = props;

  const triggerRef = useRef();
  const [_showOverlay, toggleShowOverlay] = useOverlayState(!!triggered);

  showOverlay = showOverlay !== undefined ? !!showOverlay : _showOverlay;

  useImperativeHandle(ref, () => ({
    toggleShowOverlay,
    get triggered() {
      return showOverlay;
    }
  }));

  useEffect(() => {
    if (showOverlay !== undefined && showOverlay !== _showOverlay) {
      toggleShowOverlay(!!showOverlay);
    }
  }, [_showOverlay, showOverlay, toggleShowOverlay]);

  useEffect(() => {
    if (triggerRef.current) {
      const events = toArray(triggerEvents);

      const handler = () => {
        if (!_showOverlay || triggerToggle) {
          toggleShowOverlay();
          invokeFunction(onOverlayToggle, !_showOverlay);
        }
      };

      events.forEach(event => {
        triggerRef.current.addEventListener(event, handler);
      });

      return () => {
        events.forEach(event => {
          triggerRef.current.removeEventListener(event, handler);
        });
      };
    }
  }, [onOverlayToggle, _showOverlay, toggleShowOverlay, triggerEvents, triggerRef.current]);

  const target = React.Children.only(children);

  const closeOverlay = useCallback(() => {
    toggleShowOverlay(false);
    invokeFunction(onOverlayToggle, false);
  }, [onOverlayToggle, toggleShowOverlay]);

  return (
    <React.Fragment>
      {React.cloneElement(target, {
        ...target.props,
        ref: triggerRef
      })}
      {showOverlay &&
        renderOverlay({
          origin: triggerRef,
          onClose: closeOverlay
        })}
    </React.Fragment>
  );
};

export default React.forwardRef(OverlayTrigger);
